import { Button, cn, Modal } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import {
  CheckCircle as CheckCircleIcon,
  FileArrowDown as FileArrowDownIcon,
  FilePdf as FilePdfIcon,
  Prohibit as ProhibitIcon,
} from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { toaster } from "@/app";
import { apiClient } from "@/lib/api/client";
import type { paths } from "@/lib/api/schema";
import { formatDay } from "@/lib/utils/date";
import { useUnsavedChangesGuard } from "@/lib/utils/unsaved-changes";
import { PdfViewer, type PdfViewerFile } from "../components/pdf-viewer";
import {
  type BimModelAuthor,
  countProposedMaquetteVersions,
  flattenMaquetteRows,
  MaquetteFormatButtons,
  MaquettesReceptionTable,
} from "../components/reception-sas/maquettes-table";

/**
 * Screen 017 — "Réceptionner des documents ou maquettes d'exécution via sas
 * prestataires".
 *
 * The read-only visualisation lists the versions proposed via the sas
 * (`status === "propose"`) with their date de dépôt and formats. Each proposed
 * version can be either accepted or refused (with a mandatory motivation) —
 * the two are mutually exclusive.
 * Both are staged as a local draft (amber row → emerald for accept, red for
 * refuse) and persisted in batch via "Enregistrer". Accepting clears the
 * `propose` status (it becomes a normal recorded version); refusing sets it to
 * `refuse` with its motivation.
 *
 * A document extracted from a maquette is proposed together with it, and the
 * whole proposal (the maquette version and its proposed documents) is handled
 * in one save: accepting or refusing one document stages it on every document
 * of the proposal.
 *
 * Deliberately NOT implemented:
 *  - accepting or refusing a maquette on its own: the accept/refuse routes only
 *    handle document versions;
 *  - the mail/notification-center fan-out on save: no safe recipient
 *    resolution nor notification-center table exists yet.
 */

export type ReceptionLot =
  paths["/api/yards/{yardId}/documents"]["get"]["responses"][200]["content"]["application/json"]["data"]["lots"][number];

export type ReceptionVersion =
  ReceptionLot["prestas"][number]["documents"][number]["versions"][number];

type ReceptionRow = {
  key: string;
  documentId: number;
  docNumber: string;
  docName: string;
  nature: string | null;
  zones: string[];
  authorCompanyName: string;
  lotLabel: string;
  prestaName: string;
  version: ReceptionVersion;
  isProposed: boolean;
};

const PROPOSED_STATUS = "propose";

const MOTIVATION_LEGEND =
  "Un refus est conseillé en cas de cartouche mal renseigné, d'incohérence du " +
  "document proposé avec la liste prévisionnelle, de document non exploitable " +
  "ou de problème technique sur un des fichiers joints. Si la version de " +
  "document proposée est exploitable, même si elle devait faire l'objet de " +
  "remarques techniques de la part de l'entreprise générale, elle a vocation à " +
  "être acceptée à ce stade.";

/** Count of proposals to study — the counter shown next to the tab. */
export function countProposedVersions(lots: ReceptionLot[]): number {
  let count = 0;
  for (const lot of lots) {
    for (const presta of lot.prestas) {
      for (const doc of presta.documents) {
        for (const v of doc.versions) {
          if (v.status === PROPOSED_STATUS) count += 1;
        }
      }
    }
  }
  return count;
}

/**
 * Flatten the grouped document tree into one row per version. Proposed versions
 * appear as distinct rows (no cell fusion). When `proposedOnly` is true, only
 * versions proposed via the sas are kept.
 */
export function flattenReceptionRows(
  lots: ReceptionLot[],
  proposedOnly: boolean,
): ReceptionRow[] {
  const rows: ReceptionRow[] = [];
  for (const lot of lots) {
    const lotLabel = lot.nLot ? `Lot ${lot.nLot} - ${lot.name}` : lot.name;
    for (const presta of lot.prestas) {
      for (const doc of presta.documents) {
        for (const v of doc.versions) {
          const isProposed = v.status === PROPOSED_STATUS;
          if (proposedOnly && !isProposed) continue;
          rows.push({
            key: `${doc.id}-${v.id}`,
            documentId: doc.id,
            docNumber: doc.number,
            docName: doc.name,
            nature: doc.nature,
            zones: doc.zones,
            authorCompanyName: doc.authorCompanyName,
            lotLabel,
            prestaName: presta.name,
            version: v,
            isProposed,
          });
        }
      }
    }
  }
  return rows;
}

/**
 * Version ids accepted or refused together with `versionId`: the proposed
 * versions extracted from the same maquette version, or `versionId` alone.
 */
export function proposalGroup(
  proposedRows: ReceptionRow[],
  versionId: number,
): number[] {
  const maquetteVersionId = proposedRows.find((r) => r.version.id === versionId)
    ?.version.maquette?.versionId;
  if (isNil(maquetteVersionId)) return [versionId];
  return proposedRows
    .filter((r) => r.version.maquette?.versionId === maquetteVersionId)
    .map((r) => r.version.id);
}

function formatDepotDate(iso: string | null | undefined): string {
  if (isNil(iso)) return "-";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "-";
  return formatDay(date);
}

type FetchState =
  | { status: "loading" }
  | { status: "error"; error: string }
  | { status: "success"; data: ReceptionLot[] };

type Tab = "documents" | "maquettes";

type MaquettesState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; authors: BimModelAuthor[] };

export function ReceptionSas() {
  const { yardId } = useParams<{ yardId: string }>();
  const [state, setState] = useState<FetchState>({ status: "loading" });
  const [tab, setTab] = useState<Tab>("documents");
  const [proposedOnly, setProposedOnly] = useState(true);
  const [maquettes, setMaquettes] = useState<MaquettesState>({
    status: "loading",
  });
  const [proposedMaquettesOnly, setProposedMaquettesOnly] = useState(true);
  const [pdfFile, setPdfFile] = useState<PdfViewerFile | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Draft acceptances: set of versionIds staged locally until "Enregistrer".
  const [acceptDrafts, setAcceptDrafts] = useState<Set<number>>(new Set());
  // Draft refusals: versionId -> motivation. Staged locally until "Enregistrer".
  const [rejectDrafts, setRejectDrafts] = useState<Map<number, string>>(
    new Map(),
  );
  const [motivationVersionId, setMotivationVersionId] = useState<number | null>(
    null,
  );
  const [saving, setSaving] = useState(false);

  const isDirty = acceptDrafts.size > 0 || rejectDrafts.size > 0;
  useUnsavedChangesGuard(isDirty);

  const proposedRows = useMemo(
    () =>
      state.status === "success" ? flattenReceptionRows(state.data, true) : [],
    [state],
  );

  useEffect(() => {
    void refreshKey;
    if (isNil(yardId)) return;
    let cancelled = false;

    async function fetchDocuments() {
      setState({ status: "loading" });
      const { data, error } = await apiClient.GET(
        "/api/yards/{yardId}/documents",
        { params: { path: { yardId: yardId ?? "" }, query: {} } },
      );
      if (cancelled) return;
      if (error || isNil(data)) {
        setState({ status: "error", error: "Erreur lors du chargement" });
        return;
      }
      setState({ status: "success", data: data.data.lots });
    }

    fetchDocuments();
    return () => {
      cancelled = true;
    };
  }, [yardId, refreshKey]);

  useEffect(() => {
    void refreshKey;
    if (isNil(yardId)) return;
    let cancelled = false;

    async function fetchMaquettes() {
      const { data, error } = await apiClient.GET(
        "/api/yards/{yardId}/bim-models",
        {
          params: {
            path: { yardId: yardId ?? "" },
            query: { scope: "contracts" },
          },
        },
      );
      if (cancelled) return;
      if (error || isNil(data)) {
        setMaquettes({ status: "error" });
        return;
      }
      setMaquettes({ status: "success", authors: data.data.authors });
    }

    fetchMaquettes();
    return () => {
      cancelled = true;
    };
  }, [yardId, refreshKey]);

  const handleViewPdf = useCallback(
    async (fichierPdfId: number) => {
      if (isNil(yardId)) return;
      const { data } = await apiClient.GET(
        "/api/yards/{yardId}/fichiers/{fichierId}/url",
        {
          params: {
            path: { yardId, fichierId: String(fichierPdfId) },
          },
        },
      );
      if (isNotNil(data)) setPdfFile(data.data);
    },
    [yardId],
  );

  const handleDownloadFile = useCallback(
    async (fichierId: number) => {
      if (isNil(yardId)) return;
      const { data } = await apiClient.GET(
        "/api/yards/{yardId}/fichiers/{fichierId}/url",
        {
          params: {
            path: { yardId, fichierId: String(fichierId) },
          },
        },
      );
      if (isNotNil(data)) window.open(data.data.url, "_blank");
    },
    [yardId],
  );

  const removeRejects = useCallback((versionIds: number[]) => {
    setRejectDrafts((prev) => {
      if (!versionIds.some((id) => prev.has(id))) return prev;
      const next = new Map(prev);
      for (const id of versionIds) next.delete(id);
      return next;
    });
  }, []);

  // Acceptation and refus are mutually exclusive: staging one clears the other.
  const handleToggleAccept = useCallback(
    (versionId: number) => {
      const group = proposalGroup(proposedRows, versionId);
      setAcceptDrafts((prev) => {
        const next = new Set(prev);
        const accepted = next.has(versionId);
        for (const id of group) {
          if (accepted) next.delete(id);
          else next.add(id);
        }
        return next;
      });
      removeRejects(group);
    },
    [proposedRows, removeRejects],
  );

  const handleConfirmMotivation = useCallback(
    (versionId: number, reason: string) => {
      const group = proposalGroup(proposedRows, versionId);
      setRejectDrafts((prev) => {
        const next = new Map(prev);
        for (const id of group) next.set(id, reason);
        return next;
      });
      setAcceptDrafts((prev) => {
        if (!group.some((id) => prev.has(id))) return prev;
        const next = new Set(prev);
        for (const id of group) next.delete(id);
        return next;
      });
      setMotivationVersionId(null);
    },
    [proposedRows],
  );

  const handleRemoveReject = useCallback(
    (versionId: number) => {
      removeRejects(proposalGroup(proposedRows, versionId));
      setMotivationVersionId(null);
    },
    [proposedRows, removeRejects],
  );

  const handleCancel = useCallback(() => {
    setAcceptDrafts(new Set());
    setRejectDrafts(new Map());
  }, []);

  const handleSave = useCallback(async () => {
    if (isNil(yardId) || (acceptDrafts.size === 0 && rejectDrafts.size === 0)) {
      return;
    }
    setSaving(true);
    try {
      if (acceptDrafts.size > 0) {
        const { error } = await apiClient.POST(
          "/api/yards/{yardId}/documents/accept-versions",
          {
            params: { path: { yardId } },
            body: { versionIds: Array.from(acceptDrafts) },
          },
        );
        if (isNotNil(error)) {
          toaster.error({
            title: "Acceptations non enregistrées",
            description: error.error,
          });
          return;
        }
      }

      if (rejectDrafts.size > 0) {
        const { error } = await apiClient.POST(
          "/api/yards/{yardId}/documents/reject-versions",
          {
            params: { path: { yardId } },
            body: {
              rejections: Array.from(rejectDrafts.entries()).map(
                ([versionId, reason]) => ({ versionId, reason }),
              ),
            },
          },
        );
        if (isNotNil(error)) {
          toaster.error({
            title: "Refus non enregistrés",
            description: error.error,
          });
          return;
        }
      }

      setAcceptDrafts(new Set());
      setRejectDrafts(new Map());
      setRefreshKey((k) => k + 1);
      toaster.success({
        title: "Succès",
        description: "Modifications enregistrées",
      });
    } catch (error) {
      console.error("Failed to save the sas decisions", error);
      toaster.error({
        title: "Décisions non enregistrées",
        description: "Le serveur n'a pas pu être joint. Réessayez.",
      });
    } finally {
      setSaving(false);
    }
  }, [yardId, acceptDrafts, rejectDrafts]);

  if (state.status === "loading") {
    return (
      <div className="flex items-center justify-center py-16">
        <p className="text-zinc-500">Chargement des propositions...</p>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-2">
        <p className="text-red-600">{state.error}</p>
      </div>
    );
  }

  const proposedCount = countProposedVersions(state.data);
  const rows = flattenReceptionRows(state.data, proposedOnly);
  const motivationRow =
    isNotNil(motivationVersionId) &&
    rows.find((r) => r.version.id === motivationVersionId);

  return (
    <div className="flex h-full flex-col gap-4 overflow-hidden px-4 py-4">
      {/* Visualisation toggle: Documents / Maquettes + save bar for drafts */}
      <div className="flex shrink-0 items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <TabButton
            active={tab === "documents"}
            onClick={() => setTab("documents")}
            label="Documents"
            count={proposedCount}
          />
          <TabButton
            active={tab === "maquettes"}
            onClick={() => setTab("maquettes")}
            label="Maquettes"
            count={
              maquettes.status === "success"
                ? countProposedMaquetteVersions(maquettes.authors)
                : null
            }
          />
        </div>

        {isDirty && (
          <div className="flex items-center gap-3">
            <span className="text-sm text-amber-700 dark:text-amber-400">
              {acceptDrafts.size} acceptation(s) et {rejectDrafts.size} refus en
              attente
            </span>
            <Button
              onClick={handleCancel}
              className="text-sm"
              disabled={saving}
            >
              Annuler
            </Button>
            <Button onClick={handleSave} className="text-sm" disabled={saving}>
              {saving ? "Enregistrement..." : "Enregistrer"}
            </Button>
          </div>
        )}
      </div>

      {tab === "documents" ? (
        <>
          <label className="flex shrink-0 items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
            <input
              type="checkbox"
              checked={proposedOnly}
              onChange={(e) => setProposedOnly(e.target.checked)}
            />
            Documents proposés uniquement
          </label>

          <ReceptionTable
            rows={rows}
            proposedOnly={proposedOnly}
            acceptDrafts={acceptDrafts}
            rejectDrafts={rejectDrafts}
            onViewPdf={handleViewPdf}
            onDownloadFile={handleDownloadFile}
            onToggleAccept={handleToggleAccept}
            onOpenMotivation={setMotivationVersionId}
          />
        </>
      ) : (
        <>
          <label className="flex shrink-0 items-center gap-2 text-sm text-zinc-600 dark:text-zinc-400">
            <input
              type="checkbox"
              checked={proposedMaquettesOnly}
              onChange={(e) => setProposedMaquettesOnly(e.target.checked)}
            />
            Maquettes proposées uniquement
          </label>

          {maquettes.status === "loading" ? (
            <p className="py-16 text-center text-zinc-500">
              Chargement des maquettes...
            </p>
          ) : maquettes.status === "error" ? (
            <p className="py-16 text-center text-red-600">
              Impossible de charger les maquettes.
            </p>
          ) : (
            <MaquettesReceptionTable
              rows={flattenMaquetteRows(
                maquettes.authors,
                proposedMaquettesOnly,
              )}
              proposedOnly={proposedMaquettesOnly}
              onDownload={handleDownloadFile}
            />
          )}
        </>
      )}

      {isNotNil(pdfFile) && (
        <PdfViewer file={pdfFile} onClose={() => setPdfFile(null)} />
      )}

      {motivationRow && (
        <MotivationPopup
          documentLabel={`${motivationRow.docNumber} - ${motivationRow.docName}`}
          proposalNotice={
            isNotNil(motivationRow.version.maquette)
              ? `Ce document est extrait de la maquette ${motivationRow.version.maquette.number ?? "—"} ${motivationRow.version.maquette.name}. Le refus s'appliquera à toute la proposition, c'est-à-dire à la maquette et à tous les documents qui en sont extraits.`
              : null
          }
          initialReason={rejectDrafts.get(motivationRow.version.id) ?? ""}
          alreadyDrafted={rejectDrafts.has(motivationRow.version.id)}
          onConfirm={(reason) =>
            handleConfirmMotivation(motivationRow.version.id, reason)
          }
          onRemove={() => handleRemoveReject(motivationRow.version.id)}
          onClose={() => setMotivationVersionId(null)}
        />
      )}
    </div>
  );
}

type TabButtonProps = {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number | null;
};

function TabButton({ active, onClick, label, count }: TabButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium",
        active
          ? "bg-blue-600 text-white"
          : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700",
      )}
    >
      {label}
      {isNotNil(count) && (
        <span
          className={cn(
            "rounded-full px-1.5 text-xs",
            active
              ? "bg-white/20 text-white"
              : "bg-zinc-300 text-zinc-700 dark:bg-zinc-600 dark:text-zinc-200",
          )}
        >
          {count}
        </span>
      )}
    </button>
  );
}

type ReceptionTableProps = {
  rows: ReceptionRow[];
  proposedOnly: boolean;
  acceptDrafts: Set<number>;
  rejectDrafts: Map<number, string>;
  onViewPdf: (fichierPdfId: number) => void;
  onDownloadFile: (fichierId: number) => void;
  onToggleAccept: (versionId: number) => void;
  onOpenMotivation: (versionId: number) => void;
};

function ReceptionTable({
  rows,
  proposedOnly,
  acceptDrafts,
  rejectDrafts,
  onViewPdf,
  onDownloadFile,
  onToggleAccept,
  onOpenMotivation,
}: ReceptionTableProps) {
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-zinc-500 dark:text-zinc-400">
          {proposedOnly
            ? "Aucune proposition à étudier"
            : "Aucun document à afficher"}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-0 flex-1 overflow-y-auto rounded-lg border border-zinc-200 dark:border-zinc-700">
      <table className="w-full table-fixed border-collapse text-sm">
        <colgroup>
          <col className="w-24" />
          <col />
          <col className="w-14" />
          <col className="w-24" />
          <col className="w-24" />
          <col className="w-28" />
          <col className="w-24" />
          <col className="w-16" />
          <col className="w-16" />
          <col className="w-24" />
          <col className="w-16" />
        </colgroup>
        <thead className="sticky top-0 z-10 bg-zinc-50 dark:bg-zinc-800">
          <tr className="border-b border-zinc-200 dark:border-zinc-700">
            <Th>N°</Th>
            <Th>Intitulé</Th>
            <Th>Indice</Th>
            <Th>Nature</Th>
            <Th>Zones</Th>
            <Th>Auteur</Th>
            <Th>Date de dépôt</Th>
            <Th>Formats</Th>
            <Th>BIM</Th>
            <Th>Acceptation</Th>
            <Th>Refus</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const isAccepted = acceptDrafts.has(row.version.id);
            const isRejectDrafted = rejectDrafts.has(row.version.id);
            const isRefused = row.version.status === "refuse";
            return (
              <tr
                key={row.key}
                className={cn(
                  "border-b border-zinc-100 dark:border-zinc-700/50",
                  isAccepted
                    ? "bg-emerald-50 dark:bg-emerald-950/30"
                    : isRejectDrafted
                      ? "bg-red-50 dark:bg-red-950/30"
                      : row.isProposed
                        ? "bg-amber-50 dark:bg-amber-950/30"
                        : "hover:bg-zinc-50/50 dark:hover:bg-zinc-800/20",
                )}
              >
                <td
                  className={cn(
                    "truncate px-3 py-2 font-mono text-xs",
                    row.isProposed
                      ? "font-semibold text-amber-700 dark:text-amber-400"
                      : "text-zinc-900 dark:text-zinc-100",
                  )}
                >
                  {row.docNumber}
                </td>
                <td
                  className="truncate px-3 py-2 text-zinc-800 dark:text-zinc-200"
                  title={row.docName}
                >
                  {row.docName}
                </td>
                <td className="px-3 py-2 text-zinc-600 dark:text-zinc-400 text-xs">
                  {row.version.index ?? "-"}
                </td>
                <td
                  className="truncate px-3 py-2 text-zinc-600 dark:text-zinc-400 text-xs"
                  title={row.nature ?? undefined}
                >
                  {row.nature ?? "-"}
                </td>
                <td
                  className="truncate px-3 py-2 text-zinc-600 dark:text-zinc-400 text-xs"
                  title={row.zones.join(", ")}
                >
                  {row.zones.join(", ") || "-"}
                </td>
                <td
                  className="truncate px-3 py-2 text-zinc-600 dark:text-zinc-400 text-xs"
                  title={row.authorCompanyName}
                >
                  {row.authorCompanyName || "-"}
                </td>
                <td className="px-3 py-2 text-zinc-600 dark:text-zinc-400 text-xs whitespace-nowrap">
                  {formatDepotDate(
                    row.version.proposalSentAt ?? row.version.createdAt,
                  )}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    {row.version.hasPdf &&
                    isNotNil(row.version.fichierPdfId) ? (
                      <button
                        type="button"
                        onClick={() => {
                          const id = row.version.fichierPdfId;
                          if (isNotNil(id)) onViewPdf(id);
                        }}
                        className="text-red-600 hover:text-red-700 dark:text-red-400"
                        title="Voir le PDF"
                      >
                        <FilePdfIcon size={18} weight="fill" />
                      </button>
                    ) : (
                      <span className="w-[18px]" />
                    )}
                    {row.version.hasNative &&
                    isNotNil(row.version.fichierNativeId) ? (
                      <button
                        type="button"
                        onClick={() => {
                          const id = row.version.fichierNativeId;
                          if (isNotNil(id)) onDownloadFile(id);
                        }}
                        className="text-blue-600 hover:text-blue-700 dark:text-blue-400"
                        title="Télécharger le fichier natif"
                      >
                        <FileArrowDownIcon size={18} />
                      </button>
                    ) : (
                      <span className="w-[18px]" />
                    )}
                  </div>
                </td>
                <td className="px-3 py-2">
                  {isNotNil(row.version.maquette) && (
                    <MaquetteFormatButtons
                      fichierIfcId={row.version.maquette.fichierIfcId}
                      fichierNativeId={row.version.maquette.fichierNativeId}
                      ifcTitle={`Télécharger le fichier IFC de la maquette associée ${row.version.maquette.number ?? "—"} ${row.version.maquette.name}`}
                      nativeTitle={`Télécharger le fichier natif de la maquette associée ${row.version.maquette.number ?? "—"} ${row.version.maquette.name}`}
                      onDownload={onDownloadFile}
                    />
                  )}
                </td>
                <td className="px-3 py-2">
                  {row.isProposed ? (
                    <button
                      type="button"
                      onClick={() => onToggleAccept(row.version.id)}
                      className={cn(
                        "transition-colors",
                        isAccepted
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-zinc-400 hover:text-emerald-600 dark:hover:text-emerald-400",
                      )}
                      title={
                        isAccepted
                          ? "Acceptation en attente (cliquer pour annuler)"
                          : isNotNil(row.version.maquette)
                            ? `Accepter toute la proposition : la maquette ${row.version.maquette.number ?? "—"} ${row.version.maquette.name} et tous les documents qui en sont extraits`
                            : "Accepter cette version"
                      }
                    >
                      <CheckCircleIcon
                        size={18}
                        weight={isAccepted ? "fill" : "regular"}
                      />
                    </button>
                  ) : (
                    <span className="w-[18px]" />
                  )}
                </td>
                <td className="px-3 py-2">
                  {row.isProposed ? (
                    <button
                      type="button"
                      onClick={() => onOpenMotivation(row.version.id)}
                      className={cn(
                        "transition-colors",
                        isRejectDrafted
                          ? "text-red-600 dark:text-red-400"
                          : "text-zinc-400 hover:text-red-600 dark:hover:text-red-400",
                      )}
                      title={
                        isRejectDrafted
                          ? "Refus motivé (cliquer pour modifier)"
                          : "Refuser cette version"
                      }
                    >
                      <ProhibitIcon
                        size={18}
                        weight={isRejectDrafted ? "fill" : "regular"}
                      />
                    </button>
                  ) : isRefused ? (
                    <span
                      className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400"
                      title={row.version.rejectionReason ?? undefined}
                    >
                      <ProhibitIcon size={16} weight="fill" />
                      Refusé
                    </span>
                  ) : (
                    <span className="w-[18px]" />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

type MotivationPopupProps = {
  documentLabel: string;
  proposalNotice: string | null;
  initialReason: string;
  alreadyDrafted: boolean;
  onConfirm: (reason: string) => void;
  onRemove: () => void;
  onClose: () => void;
};

function MotivationPopup({
  documentLabel,
  proposalNotice,
  initialReason,
  alreadyDrafted,
  onConfirm,
  onRemove,
  onClose,
}: MotivationPopupProps) {
  const [reason, setReason] = useState(initialReason);
  const trimmed = reason.trim();

  return (
    <Modal.Root open onOpenChange={(e) => !e.open && onClose()}>
      <Modal.Backdrop />
      <Modal.Positioner>
        <Modal.Content size="lg">
          <Modal.Header>
            <Modal.Title>Motivation refus</Modal.Title>
          </Modal.Header>

          <div className="px-6 pb-6">
            <p className="mb-3 text-sm font-medium text-zinc-700 dark:text-zinc-300">
              {documentLabel}
            </p>
            <p className="mb-4 text-xs italic text-zinc-500 dark:text-zinc-400">
              {MOTIVATION_LEGEND}
            </p>
            {isNotNil(proposalNotice) && (
              <p className="mb-4 text-sm font-medium text-red-700 dark:text-red-400">
                {proposalNotice}
              </p>
            )}
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              required
              rows={4}
              placeholder="Motif du refus"
              className="w-full rounded-md border border-zinc-300 bg-transparent px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 dark:border-zinc-600 dark:text-zinc-100"
            />

            <div className="mt-6 flex items-center justify-between gap-3">
              <div>
                {alreadyDrafted && (
                  <Button
                    onClick={onRemove}
                    className="text-sm text-red-600 dark:text-red-400"
                  >
                    Retirer le refus
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-3">
                <Button onClick={onClose} className="text-sm">
                  Annuler
                </Button>
                <Button
                  onClick={() => {
                    // Motivation obligatoire: jamais de refus sans motif.
                    if (trimmed.length > 0) onConfirm(trimmed);
                  }}
                  disabled={trimmed.length === 0}
                  className="text-sm"
                >
                  Refuser
                </Button>
              </div>
            </div>
          </div>
        </Modal.Content>
      </Modal.Positioner>
    </Modal.Root>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300">
      {children}
    </th>
  );
}
